import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Mail, ChevronDown, ChevronUp, AlertTriangle, ExternalLink, PackageCheck } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Link } from "react-router-dom";
import api from "../../../lib/api";
import ReceiveOrderDialog from "../ReceiveOrderDialog";

/**
 * Banner que aparece cuando hay borradores de pedido auto-generados
 * pendientes de revisar/enviar. Permite envío manual con override de email.
 *
 * Aparece sólo si hay drafts → cero ruido cuando no hay nada pendiente.
 */
export default function AutoReorderDraftsBanner({ restaurantId }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState({});  // {order_id: bool}
  const [emailOverrides, setEmailOverrides] = useState({});  // {order_id: string}
  const [receiveOrder, setReceiveOrder] = useState(null);

  const load = async () => {
    if (!restaurantId) return;
    try {
      const all = await api.request(`/restaurants/${restaurantId}/orders`);
      const filtered = (Array.isArray(all) ? all : [])
        .filter(o => o.status === "draft" && o.source === "auto_reorder")
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setDrafts(filtered);
    } catch {
      // silent — banner is opcional
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [restaurantId]);  // eslint-disable-line

  const handleSend = async (order) => {
    const oid = order.order_id;
    setSending(s => ({ ...s, [oid]: true }));
    try {
      // If user typed an override, save it first
      const override = (emailOverrides[oid] || "").trim();
      if (override && override !== order.recipient_email_override) {
        await api.request(`/orders/${oid}`, {
          method: "PATCH",
          body: JSON.stringify({ recipient_email_override: override }),
        });
      }
      const res = await api.request(`/orders/${oid}/send`, { method: "POST" });
      toast.success(`Enviado a ${res.recipient}`);
      await load();
    } catch (e) {
      const msg = e?.message || "";
      if (msg.includes("422") || msg.toLowerCase().includes("email")) {
        toast.error("No hay email del proveedor — añade uno en el campo de abajo y vuelve a intentar");
      } else {
        toast.error("Error al enviar");
      }
    } finally {
      setSending(s => ({ ...s, [oid]: false }));
    }
  };

  if (loading || drafts.length === 0) return null;

  const totalAmount = drafts.reduce((s, d) => s + (d.total_amount || 0), 0);

  return (
    <div
      className="border border-orange-300 bg-orange-50 dark:bg-orange-950/20 rounded-xl overflow-hidden"
      data-testid="auto-reorder-drafts-banner"
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-orange-100/50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-orange-900 dark:text-orange-100">
              {drafts.length} {drafts.length === 1 ? "propuesta" : "propuestas"} de pedido pendiente{drafts.length === 1 ? "" : "s"} de revisar
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-300">
              {totalAmount.toFixed(2)}€ en total · generadas automáticamente al detectar ingredientes bajo mínimos
            </p>
          </div>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-orange-700 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-orange-700 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-orange-200 bg-card dark:bg-background divide-y">
          {drafts.map(order => {
            const oid = order.order_id;
            const isSending = sending[oid];
            const hasEmail = !!(order.recipient_email_override || (order.supplier_id && order.supplier_id !== "_no_supplier"));
            return (
              <div key={oid} className="p-4 space-y-3" data-testid={`draft-order-row-${oid}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">
                      {order.supplier_name || "Proveedor sin asignar"}
                      <span className="text-xs text-muted-foreground ml-2">#{oid.slice(-8)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {order.items?.length || 0} ítems · {(order.total_amount || 0).toFixed(2)}€
                      {order.notes && <span className="block mt-1 italic">"{order.notes}"</span>}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSend(order)}
                    disabled={isSending}
                    data-testid={`draft-order-send-${oid}`}
                    className="bg-orange-600 hover:bg-orange-700 text-white flex-shrink-0"
                  >
                    {isSending
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Send className="w-3.5 h-3.5 mr-1.5" /> Enviar</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReceiveOrder(order)}
                    disabled={isSending}
                    data-testid={`draft-order-receive-${oid}`}
                    className="flex-shrink-0"
                    title="Recibir directamente sin enviar email"
                  >
                    <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Recibir
                  </Button>
                </div>

                {/* Items preview */}
                <ul className="text-xs space-y-0.5 pl-4 max-h-32 overflow-auto">
                  {(order.items || []).slice(0, 5).map((it, i) => (
                    <li key={i} className="text-muted-foreground">
                      • {it.name} — {it.qty} {it.unit || ""} · {(it.expected_total || 0).toFixed(2)}€
                    </li>
                  ))}
                  {(order.items || []).length > 5 && (
                    <li className="text-muted-foreground italic">…y {order.items.length - 5} más</li>
                  )}
                </ul>

                {/* Email override */}
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder={hasEmail ? "Sobreescribir email del proveedor (opcional)" : "Email del proveedor (obligatorio)"}
                    defaultValue={order.recipient_email_override || ""}
                    onChange={(e) => setEmailOverrides(o => ({ ...o, [oid]: e.target.value }))}
                    className={`text-xs h-8 flex-1 ${!hasEmail ? "border-amber-400" : ""}`}
                    data-testid={`draft-order-email-input-${oid}`}
                  />
                </div>
                {!hasEmail && !emailOverrides[oid] && (
                  <p className="text-[11px] text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    El proveedor no tiene email. Añade uno aquí o configúralo en su ficha.
                  </p>
                )}
              </div>
            );
          })}

          <div className="p-3 bg-muted/30">
            <Link
              to="/app/settings?tab=auto-reorder"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="auto-reorder-config-link"
            >
              <ExternalLink className="w-3 h-3" /> Configurar envío automático en Ajustes
            </Link>
          </div>
        </div>
      )}

      <ReceiveOrderDialog
        open={!!receiveOrder}
        onOpenChange={(o) => { if (!o) setReceiveOrder(null); }}
        order={receiveOrder}
        onSuccess={() => { setReceiveOrder(null); load(); }}
      />
    </div>
  );
}
